import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import { AppConfigService } from '../services/app-config.service';
import {
  ChallengeWindowDimensions,
  PayerAuthSetup,
  PayerAuthSetupResponse,
  PaymentAuthorizationRequest,
  PaymentAuthorizationResponse,
  ValidateAuthAndAuthorizeRequest,
} from '../models/payer-auth.model';
import {finalize, tap} from 'rxjs/operators';
export enum CheckoutType {
  WebSale,
  WebPayment,
}

@Injectable({
  providedIn: 'root',
})
export class PayerAuthService {
  public isPayerAuthPending$ = new BehaviorSubject<boolean>(false);
  private readonly baseUrl: string;

  private challengeWindowSizeMap = new Map<string, ChallengeWindowDimensions>([
    ['00', { width: '400', height: '400' }],
    ['01', { width: '250', height: '400' }],
    ['02', { width: '390', height: '400' }],
    ['03', { width: '500', height: '600' }],
    ['04', { width: '600', height: '400' }],
    ['05', { width: '100%', height: '100%' }],
  ]);

  private payerAuthPathMap = new Map<CheckoutType, string>([
    [CheckoutType.WebPayment, '/cart/cscheckout/'],
    [CheckoutType.WebSale, '/cart/checkout/'],
  ]);

  constructor(private http: HttpClient, private appConfigService: AppConfigService) {
    this.baseUrl = `${this.appConfigService.vistekUrl}`;
  }

  public setupPayerAuth(checkoutType: CheckoutType, model: PayerAuthSetup): Observable<PayerAuthSetupResponse> {
    this.isPayerAuthPending$.next(true);
    
    return this.http.post<PayerAuthSetupResponse>(
      `${this.baseUrl}${this.payerAuthPathMap.get(checkoutType)}setupPayerAuth`,
      model
    ).pipe(finalize(() => {
      this.isPayerAuthPending$.next(false);
    }));
  }

  public setupPayerAuthCof(checkoutType: CheckoutType): Observable<PayerAuthSetupResponse> {
    this.isPayerAuthPending$.next(true);
    
    return this.http.post<PayerAuthSetupResponse>(
      `${this.baseUrl}${this.payerAuthPathMap.get(checkoutType)}setupPayerAuthCof`, null
    ).pipe(finalize(() => {
      this.isPayerAuthPending$.next(false);
    }));
  }

  public authorizePayment(
    checkoutType: CheckoutType,
    model: PaymentAuthorizationRequest
  ): Observable<PaymentAuthorizationResponse> {
    model.BrowserScreenHeight = window.screen.height.toString();
    model.BrowserScreenWidth = window.screen.width.toString();
    
    this.isPayerAuthPending$.next(true);
    
    return this.http
      .post<PaymentAuthorizationResponse>(
        `${this.baseUrl}${this.payerAuthPathMap.get(checkoutType)}authorizePayment`,
        model
      )
      .pipe(
        tap((res) => {
          this.enrichAuthorizationResponse(res);
        })
      ).pipe(finalize(() => {
        this.isPayerAuthPending$.next(false);
      }));
  }

  public validatePayerAuth(
    checkoutType: CheckoutType,
    transactionId: string,
    model: PaymentAuthorizationRequest
  ): Observable<PaymentAuthorizationResponse> {
    const subForTokenElement = document.getElementById('savePaymentDetailsForLater');
    const subForDefaultTokenElement = document.getElementById('setDefaultPaymentMethod');

    let requestModel: ValidateAuthAndAuthorizeRequest = {
      TransactionId: transactionId,
      Account: model.Account,
      OrderDetails: model.OrderDetails,
      TransactionMode: model.TransactionMode,
      BrowserScreenHeight: window.screen.height.toString(),
      BrowserScreenWidth: window.screen.width.toString(),
      ShouldCreateNetworkToken: (subForTokenElement as HTMLInputElement)?.checked,
      ShouldSetDefaultNetworkToken: (subForDefaultTokenElement as HTMLInputElement)?.checked,
      InstrumentIdentifierId: model.InstrumentIdentifierId
    };

    this.isPayerAuthPending$.next(true);
    
    return this.http.post<PaymentAuthorizationResponse>(
      `${this.baseUrl}${this.payerAuthPathMap.get(checkoutType)}ValidatePayerAuth`,
      requestModel
    ).pipe(finalize(() => {
      this.isPayerAuthPending$.next(false);
    }));
  }

  private enrichAuthorizationResponse(resp: PaymentAuthorizationResponse) {
    if (resp.Challenge) {
      resp.Challenge.windowDimensions = this.challengeWindowSizeMap.get(resp.Challenge.ChallengeWindowSize);
    }
  }
}
